import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Grid2,
  TextField,
  Button,
  ButtonGroup,
  FormControlLabel,
  Switch
} from "@mui/material";
import StoryCard from "../components/StoryCard";
import { fetchStoriesForEditor, fetchDraftStoriesForEditor } from "../utils/api";
import StoryRow from "../components/StoryRow";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";


const sortOptions = [
  { value: "updatedAt", label: "Updated Date" },
  { value: "publishedAt", label: "Published Date" },
  { value: "title", label: "Title" },
];

const EditorialStoriesDirectoryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [viewType, setViewType] = useState("card"); // "card" or "row"


  const filter = searchParams.get("filter") || "";
  const searchQuery = searchParams.get("q") || "";
  const sortField = searchParams.get("sortBy") || "updatedAt";
  const sortOrder = searchParams.get("order") || "desc";

  const categories = ["Romantik", "Fantastik", "Macera", "Dram", "Gençlik", "Bilim Kurgu", "Smut"];

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [searchFields, setSearchFields] = useState(["title"]); // Default search in title only

  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [allStories, setAllStories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [clientPage, setClientPage] = useState(1);
  const pageSize = 20;

  const loadMoreRef = useRef(null);

  const [showDrafts, setShowDrafts] = useState(false);
  const [draftStories, setDraftStories] = useState([]);


  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let fetchedStories = [];

      if (showDrafts) {
        // Fetch draft stories and filter out any published ones
        const drafts = await fetchDraftStoriesForEditor({ limit: 10000 });
        console.log("Fetched draft stories:", drafts);

        // Filter drafts to exclude those with a "PUBLISHED" status
        fetchedStories = drafts.filter(story => story.isPublished == false);
        setAllStories(fetchedStories);
      } else {
        const stories = await fetchStoriesForEditor({ limit: 10000 });
        fetchedStories = stories.filter(
          story => story.type === "text" && story.isPublished === true
        );
        console.log("Fetched published stories:", fetchedStories);
        setAllStories(fetchedStories);
      }

      setClientPage(1);
    } catch (error) {
      console.error("Error fetching stories:", error);
      setAllStories([]);
    } finally {
      setLoading(false);
    }
  }, [showDrafts]);


  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateQueryParam = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    value ? newParams.set(key, value) : newParams.delete(key);
    setSearchParams(newParams);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    updateQueryParam("q", localSearch.trim());
  };

  const handleFilterChange = (newFilter) => {
    updateQueryParam("filter", newFilter);
  };

  const handleSortOptionClick = (optionValue) => {
    const newParams = new URLSearchParams(searchParams);

    if (sortField === optionValue) {
      const newOrder = sortOrder === "desc" ? "asc" : "desc";
      newParams.set("order", newOrder);
    } else {
      newParams.set("sortBy", optionValue);
      newParams.set("order", "desc"); // Reset order to default when changing fields
    }

    setSearchParams(newParams);
  };

  const filterGroupStories = useMemo(() => {
    return allStories.filter((story) => {
      if (filter === "contracted") return story.isPublished && !story.isUGC;
      if (filter === "user") return story.isUGC;
      if (filter === "flagged") return story.isFlagged;
      return true; // default "all"
    });
  }, [allStories, filter]);

  const filteredStories = useMemo(() => {
    return allStories.filter((story) => {
      let matchesFilter = true;
      if (filter === "contracted") matchesFilter = story.isPublished && !story.isUGC;
      else if (filter === "user") matchesFilter = story.isUGC;
      else if (filter === "flagged") matchesFilter = story.isFlagged;

      let matchesSearch = true;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        matchesSearch = searchFields.some((field) => {
          const fieldValue = story[field];
          return fieldValue?.toLowerCase().includes(q);
        });
      }

      let matchesCategories = selectedCategories.length === 0 ||
        (story.tags && selectedCategories.some(category => story.tags.includes(category)));

      return matchesFilter && matchesSearch && matchesCategories;
    });
  }, [allStories, filter, searchQuery, searchFields, selectedCategories]);


  const sortedStories = useMemo(() => {
    console.log("Sorting by field:", sortField, "with order:", sortOrder);
    return filteredStories.slice().sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (aVal && aVal._seconds) {
        aVal = aVal._seconds;
        console.log(`a (${a.id}) converted timestamp: ${aVal}`);
      }
      if (bVal && bVal._seconds) {
        bVal = bVal._seconds;
        console.log(`b (${b.id}) converted timestamp: ${bVal}`);
      }

      console.log(`Comparing values - a: ${aVal}, b: ${bVal}`);

      if (typeof aVal === "string" && typeof bVal === "string") {
        const result = sortOrder === "desc"
          ? bVal.localeCompare(aVal)
          : aVal.localeCompare(bVal);
        console.log(`String comparison result: ${result}`);
        return result;
      }

      const numericResult = sortOrder === "desc" ? bVal - aVal : aVal - bVal;
      console.log(`Numeric comparison result: ${numericResult}`);
      return numericResult;
    });
  }, [filteredStories, sortField, sortOrder]);


  const visibleStories = sortedStories.slice(0, clientPage * pageSize);

  useEffect(() => {
    if (loading || !loadMoreRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleStories.length < sortedStories.length) {
          setClientPage((prev) => prev + 1);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [loading, visibleStories.length, sortedStories.length]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4">Editorial Stories Directory</Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showDrafts}
              onChange={(e) => setShowDrafts(e.target.checked)}
              color="success"
            />
          }
          label="Show Drafts"
        />
        <Typography variant="body2" sx={{ ml: 2 }}>
          {loading
            ? "Loading stories..."
            : `Showing ${filteredStories.length} of ${filterGroupStories.length} ${showDrafts
              ? "draft stories"
              : filter === "user"
                ? "published UGC stories"
                : filter === "contracted"
                  ? "published Contracted stories"
                  : filter === "flagged"
                    ? "flagged stories"
                    : "published stories"
            }`}
        </Typography>

      </Box>



      {/* Search Bar + Search Fields */}
      <Box component="form" onSubmit={handleSearchSubmit} sx={{ mb: 1 }}>
        <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
          <TextField
            label="Search stories"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            fullWidth
          />
          <Button type="submit" variant="contained">Search</Button>
        </Box>

        {/* Search Fields Toggle placed directly below the search bar */}
        <Typography variant="body2" sx={{ mb: 1 }}>
          Search in:
        </Typography>
        <ButtonGroup sx={{ mb: 2 }} variant="outlined">
          {["title", "writerName", "description"].map((field) => (
            <Button
              key={field}
              variant={searchFields.includes(field) ? "contained" : "outlined"}
              onClick={() =>
                setSearchFields((prev) =>
                  prev.includes(field)
                    ? prev.filter((f) => f !== field)
                    : [...prev, field]
                )
              }
            >
              {field.charAt(0).toUpperCase() + field.slice(1)}
            </Button>
          ))}
        </ButtonGroup>
      </Box>

      {/* Filters */}
      <ButtonGroup sx={{ mb: 1 }}>
        {["", "contracted", "user", "flagged"].map((f) => (
          <Button
            key={f}
            variant={filter === f ? "contained" : "outlined"}
            onClick={() => handleFilterChange(f)}
          >
            {f || "All"}
          </Button>
        ))}
      </ButtonGroup>

      {/* Category Selector */}
      <Typography variant="body2" sx={{ mb: 1 }}>
        Categories:
      </Typography>
      <ButtonGroup sx={{ mb: 1 }} variant="outlined">
        {categories.map((category) => (
          <Button
            key={category}
            variant={selectedCategories.includes(category) ? "contained" : "outlined"}
            onClick={() =>
              setSelectedCategories((prev) =>
                prev.includes(category)
                  ? prev.filter((c) => c !== category)
                  : [...prev, category]
              )
            }
          >
            {category}
          </Button>
        ))}
      </ButtonGroup>

      {/* Sorting Buttons and View Toggle */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
        <ButtonGroup variant="outlined">
          <Button
            variant={viewType === "card" ? "contained" : "outlined"}
            onClick={() => setViewType("card")}
          >
            <ViewModuleIcon />
          </Button>
          <Button
            variant={viewType === "row" ? "contained" : "outlined"}
            onClick={() => setViewType("row")}
          >
            <ViewListIcon />
          </Button>
        </ButtonGroup>

        {viewType === "card" && (
          <>
            <Typography variant="body2">Sort by:</Typography>
            <ButtonGroup>
              {sortOptions.map((option) => (
                <Button
                  key={option.value}
                  variant={sortField === option.value ? "contained" : "outlined"}
                  onClick={() => handleSortOptionClick(option.value)}
                >
                  {option.label}{" "}
                  {sortField === option.value ? (sortOrder === "desc" ? "↓" : "↑") : ""}
                </Button>
              ))}
            </ButtonGroup>
          </>
        )}
      </Box>


      {/* Stories Display */}
      {loading ? (
        <CircularProgress />
      ) : visibleStories.length === 0 ? (
        <Typography>No stories found.</Typography>
      ) : viewType === "card" ? (
        <Grid2 container spacing={2}>
          {visibleStories.map((story) => (
            <Grid2 item xs={12} sm={6} md={4} key={story.id}>
              <StoryCard story={story} />
            </Grid2>
          ))}
        </Grid2>
      ) : (
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1,
              borderBottom: "2px solid",
              borderColor: "divider",
              bgcolor: "background.default",
            }}
          >
            {[
              { label: "Title", value: "title", width: "20%" },
              { label: "Writer", value: "writerName", width: "15%" },
              { label: "Published", value: "isPublished", width: "10%" },
              { label: "Active", value: "isActive", width: "10%" },
              { label: "Flagged", value: "isFlagged", width: "10%" },
              { label: "Published At", value: "publishedAt", width: "15%" },
              { label: "Updated At", value: "updatedAt", width: "15%" },
              { label: "Priority", value: "priority", width: "5%" },
            ].map(({ label, value, width }) => (
              <Button
                key={label}
                size="small"
                variant="text"
                sx={{
                  width,
                  justifyContent: "flex-start",
                  color: "text.primary",
                  textTransform: "none",
                  fontWeight: "bold",
                }}
                disabled={!["title", "writerName", "publishedAt", "updatedAt"].includes(value)}
                onClick={() => ["title", "writerName", "publishedAt", "updatedAt"].includes(value) && handleSortOptionClick(value)}
                endIcon={
                  ["title", "writerName", "publishedAt", "updatedAt"].includes(value) && sortField === value ? (
                    <Typography variant="body2" sx={{ color: "inherit" }}>
                      {sortOrder === "desc" ? "↓" : "↑"}
                    </Typography>
                  ) : null
                }
              >
                {label}
              </Button>
            ))}
          </Box>
          {visibleStories.map((story) => (
            <StoryRow key={story.id} story={story} />
          ))}
        </Box>
      )}

      <Box ref={loadMoreRef} sx={{ height: 1 }} />
    </Box>
  );
};

export default EditorialStoriesDirectoryPage;
